import React, { useState, useEffect } from 'react';
import './Results.css';
import collegeConfig from '../../config/collegeConfig';
import {
  FaChartBar,
  FaArrowLeft,
  FaCheckCircle,
  FaTrophy,
  FaClipboardList,
  FaCalendarAlt,
  FaBook,
  FaChartLine,
  FaAward,
  FaInfoCircle,
  FaFileAlt,
  FaPaperPlane,
  FaExclamationTriangle
} from 'react-icons/fa';

const Results = ({ onNavigate }) => {
  const [assessmentResults, setAssessmentResults] = useState([]);
  const [checklistResults, setChecklistResults] = useState([]);
  const [internalTests, setInternalTests] = useState([]);
  const [internalTestEnabled, setInternalTestEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('assessments');
  const [selectedResult, setSelectedResult] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [selfSubmitTestId, setSelfSubmitTestId] = useState(null);
  const [selfMarksInput, setSelfMarksInput] = useState('');
  const [selfSubmitError, setSelfSubmitError] = useState('');
  const [selfSubmitSaving, setSelfSubmitSaving] = useState(false);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
        const parsedUser = JSON.parse(userData);
        fetchResults(parsedUser);
    }

    fetch(`${collegeConfig.api.baseURL}/system-settings`)
      .then(r => r.json())
      .then(d => { if (d.success) setInternalTestEnabled(d.internalTestEnabled !== false); })
      .catch(() => {});
  }, []);

  const fetchResults = async (userData) => {
    try {
      const token = localStorage.getItem('token');
      const studentId = userData.id || userData._id;

      try {
        const assessmentResponse = await fetch(
          `${collegeConfig.api.baseURL}/assessments/student/${studentId}/submissions`,
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );

        if (assessmentResponse.ok) {
          const assessmentData = await assessmentResponse.json();
          setAssessmentResults(assessmentData.submissions || []);
        } else {
          console.error('Assessment fetch failed:', assessmentResponse.status);
        }
      } catch (assessmentError) {
        console.error('Error fetching assessments:', assessmentError);
      }

      try {
        const checklistResponse = await fetch(
          `${collegeConfig.api.baseURL}/checklist-evaluations/student/${studentId}/evaluations`,
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );

        if (checklistResponse.ok) {
          const checklistData = await checklistResponse.json();
          setChecklistResults(checklistData.evaluations || []);
        } else {
          console.error('Checklist fetch failed:', checklistResponse.status);
        }
      } catch (checklistError) {
        console.error('Error fetching checklists:', checklistError);
      }

      try {
        const internalTestsResponse = await fetch(
          `${collegeConfig.api.baseURL}/internal-tests/student/my-tests`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (internalTestsResponse.ok) {
          const itData = await internalTestsResponse.json();
          setInternalTests(itData.tests || []);
        }
      } catch (itError) {
        console.error('Error fetching internal tests:', itError);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching results:', error);
      setError('Failed to load results. Please try again later.');
      setLoading(false);
    }
  };

  const handleSelfSubmit = async (test) => {
    setSelfSubmitError('');
    const val = Number(selfMarksInput);
    if (selfMarksInput === '' || isNaN(val)) {
      setSelfSubmitError('Please enter valid marks.');
      return;
    }
    if (val < 0 || val > test.maxMarks) {
      setSelfSubmitError(`Marks must be between 0 and ${test.maxMarks}.`);
      return;
    }
    setSelfSubmitSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${collegeConfig.api.baseURL}/internal-tests/${test._id}/self-submit`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ selfMarks: val })
        }
      );
      const data = await res.json();
      if (data.success) {
        setSelfSubmitTestId(null);
        setSelfMarksInput('');
        // Refresh
        const userData = localStorage.getItem('user');
        if (userData) fetchResults(JSON.parse(userData));
      } else {
        setSelfSubmitError(data.message || 'Failed to submit.');
      }
    } catch (err) {
      setSelfSubmitError('Error submitting marks.');
    }
    setSelfSubmitSaving(false);
  };

  const getGradeColor = (grade) => {
    const gradeColors = {
      'A': '#2ECC71',
      'B': '#3498DB',
      'C': '#F39C12',
      'D': '#E67E22',
      'E': '#E74C3C',
      'F': '#C0392B',
      'G': '#9B59B6',
      'H': '#E67E22',
      'I': '#E74C3C',
      'J': '#C0392B'
    };
    return gradeColors[grade] || '#95A5A6';
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'submitted': { color: '#3498DB', text: 'Submitted' },
      'reviewed': { color: '#2ECC71', text: 'Reviewed' },
      'draft': { color: '#95A5A6', text: 'Draft' },
      'completed': { color: '#2ECC71', text: 'Completed' },
      'approved': { color: '#27AE60', text: 'Approved' }
    };
    const config = statusConfig[status] || { color: '#95A5A6', text: status };
    return (
      <span style={{
        background: config.color,
        color: 'white',
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '600'
      }}>
        {config.text}
      </span>
    );
  };

  const viewAssessmentDetails = async (submission) => {
    setSelectedResult({ ...submission, type: 'assessment' });
    setViewMode('detail');
  };

  const viewChecklistDetails = (evaluation) => {
    setSelectedResult({ ...evaluation, type: 'checklist' });
    setViewMode('detail');
  };

  const calculateAverageScore = (grades) => {
    if (!grades || grades.length === 0) return null;
    
    let totalScore = 0;
    let itemCount = 0;
    
    grades.forEach(period => {
      period.items?.forEach(item => {
        if (item.score !== null && item.score !== undefined) {
          totalScore += item.score;
          itemCount++;
        }
      });
    });
    
    return itemCount > 0 ? (totalScore / itemCount).toFixed(2) : null;
  };

  const renderAssessmentsList = () => {
    if (assessmentResults.length === 0) {
      return (
        <div className="empty-state">
          <FaChartBar size={64} color="#BDC3C7" />
          <h3>No Assessment Results</h3>
          <p>Your assessment results will appear here once graded by teachers.</p>
        </div>
      );
    }

    return (
      <div className="results-grid">
        {assessmentResults.map((result) => {
          const avgScore = calculateAverageScore(result.grades);
          const isYearly = result.assessmentType === 'yearly';
          const isOverall = result.assessmentType === 'overall';
          
          return (
            <div 
              key={result.submissionId} 
              className="result-card"
              onClick={() => viewAssessmentDetails(result)}
            >
              <div className="result-card-header">
                <div className="result-icon" style={{ 
                  background: isYearly ? '#3498DB' : '#9B59B6' 
                }}>
                  <FaBook />
                </div>
                <div className="result-title-section">
                  <h3>{result.assessmentName}</h3>
                  <p className="course-name">{result.courseName} ({result.courseCode})</p>
                </div>
              </div>

              <div className="result-card-body">
                <div className="result-info-row">
                  <span className="label">Type:</span>
                  <span className="value">
                    {isYearly ? 'Yearly (Score-based)' : 'Grade-based'}
                  </span>
                </div>
                <div className="result-info-row">
                  <span className="label">Unit:</span>
                  <span className="value">{result.unit || 'N/A'}</span>
                </div>
                <div className="result-info-row">
                  <span className="label">Teacher:</span>
                  <span className="value">{result.teacherName}</span>
                </div>
                
                {result.grades && result.grades.length > 0 && (
                  <div className="result-summary">
                    {isYearly && avgScore && (
                      <div className="summary-item">
                        <span className="summary-label">Avg Score</span>
                        <span className="summary-value">
                          {avgScore}/10
                        </span>
                      </div>
                    )}
                    {isOverall && (
                      <div className="summary-item">
                        <span className="summary-label">Items Graded</span>
                        <span className="summary-value">
                          {result.grades[0]?.items?.filter(i => i.grade).length || 0}
                        </span>
                      </div>
                    )}
                    <div className="summary-item">
                      <span className="summary-label">Periods</span>
                      <span className="summary-value">
                        {result.grades.length}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="result-card-footer">
                {getStatusBadge(result.status)}
                <span className="result-date">
                  <FaCalendarAlt size={12} />
                  {new Date(result.submittedAt).toLocaleDateString()}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderChecklistsList = () => {
    if (checklistResults.length === 0) {
      return (
        <div className="empty-state">
          <FaClipboardList size={64} color="#BDC3C7" />
          <h3>No Checklist Evaluations</h3>
          <p>Your checklist evaluations will appear here once completed by teachers.</p>
        </div>
      );
    }

    return (
      <div className="results-grid">
        {checklistResults.map((result) => (
          <div 
            key={result._id} 
            className="result-card"
            onClick={() => viewChecklistDetails(result)}
          >
            <div className="result-card-header">
              <div className="result-icon" style={{ background: '#9B59B6' }}>
                <FaClipboardList />
              </div>
              <div className="result-title-section">
                <h3>{result.checklistName}</h3>
                <p className="course-name">
                  {result.courseId?.name} ({result.courseId?.code})
                </p>
              </div>
            </div>

            <div className="result-card-body">
              <div className="result-info-row">
                <span className="label">Academic Year:</span>
                <span className="value">{result.academicYear}</span>
              </div>
              <div className="result-info-row">
                <span className="label">Evaluator:</span>
                <span className="value">
                  {result.evaluatedBy?.profile?.firstName} {result.evaluatedBy?.profile?.lastName}
                </span>
              </div>
              <div className="result-summary">
                <div className="summary-item">
                  <span className="summary-label">Score</span>
                  <span className="summary-value">
                    {result.totalScore}/{result.maxPossibleScore}
                  </span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Percentage</span>
                  <span className="summary-value">
                    {result.percentageScore?.toFixed(1)}%
                  </span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Grade</span>
                  <span 
                    className="summary-value grade-badge"
                    style={{ 
                      background: getGradeColor(result.grade),
                      color: 'white'
                    }}
                  >
                    {result.grade}
                  </span>
                </div>
              </div>
            </div>

            <div className="result-card-footer">
              {getStatusBadge(result.status)}
              <span className="result-date">
                <FaCalendarAlt size={12} />
                {new Date(result.submittedAt).toLocaleDateString()}
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderInternalTests = () => {
    if (internalTests.length === 0) {
      return (
        <div className="empty-state">
          <FaFileAlt size={64} color="#BDC3C7" />
          <h3>No Internal Tests</h3>
          <p>Internal tests for your department will appear here once created by your teacher.</p>
        </div>
      );
    }

    return (
      <div>
        <p style={{ color: '#fff', marginBottom: 20, fontSize: 14 }}>
          All internal tests in your department. If you have not been graded for a test, you can self-submit your marks for teacher verification.
        </p>
        <div className="results-grid">
          {internalTests.map((test, idx) => {
            const mark = test.myMark;
            const isGraded = mark && mark.gradedBy;
            const isAbsent = mark && mark.isAbsent && !( mark.isSelfSubmitted && mark.verificationStatus === 'verified');
            const isSelfPending = mark && mark.isSelfSubmitted && mark.verificationStatus === 'pending' && !isGraded;
            const isSelfVerified = mark && mark.isSelfSubmitted && mark.verificationStatus === 'verified';
            const isSelfRejected = mark && mark.isSelfSubmitted && mark.verificationStatus === 'rejected';
            const canSelfSubmit = !isGraded && (!mark || !mark.isSelfSubmitted);

            let marksValue = null;
            let marksColor = '#aaa';
            let statusEl = null;

            if (isAbsent) {
              marksValue = 'ABSENT';
              marksColor = '#E74C3C';
              statusEl = <span style={itStyles.badge('#E74C3C')}>Absent</span>;
            } else if (isGraded || isSelfVerified) {
              marksValue = `${mark.marksObtained} / ${test.maxMarks}`;
              marksColor = '#2ECC71';
              statusEl = <span style={itStyles.badge('#2ECC71')}>{isGraded ? 'Graded' : 'Verified'}</span>;
            } else if (isSelfPending) {
              marksValue = `${mark.selfMarks} / ${test.maxMarks}`;
              marksColor = '#E67E22';
              statusEl = <span style={itStyles.badge('#F39C12')}>Pending Verification</span>;
            } else if (isSelfRejected) {
              marksValue = 'Rejected';
              marksColor = '#E74C3C';
              statusEl = <span style={itStyles.badge('#E74C3C')}>Rejected</span>;
            } else {
              marksValue = '—';
              statusEl = <span style={{ color: '#bbb', fontSize: 12 }}>Not graded</span>;
            }

            const isSelfFormOpen = selfSubmitTestId === test._id;

            return (
              <div key={test._id} className="result-card" style={{ cursor: 'default' }}>
                <div className="result-card-header" style={{ flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                  <div className="result-icon" style={{ background: '#E67E22' }}>
                    <FaFileAlt />
                  </div>
                  <div className="result-title-section">
                    <h3>{test.testName}</h3>
                    <p className="course-name">
                      <FaCalendarAlt size={11} style={{ marginRight: 4 }} />
                      {new Date(test.date).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                <div className="result-card-body">
                  <div className="result-summary">
                    <div className="summary-item">
                      <span className="summary-label">Max Marks</span>
                      <span className="summary-value">{test.maxMarks}</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Your Marks</span>
                      <span className="summary-value" style={{ color: marksColor }}>{marksValue}</span>
                    </div>
                  </div>

                  {/* Self-submit form / buttons */}
                  {(canSelfSubmit || isSelfRejected) && (
                    <div style={{ marginTop: 14 }}>
                      {isSelfFormOpen ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                            <input
                              type="number"
                              min={0}
                              max={test.maxMarks}
                              value={selfMarksInput}
                              onChange={e => { setSelfMarksInput(e.target.value); setSelfSubmitError(''); }}
                              placeholder={`0 – ${test.maxMarks}`}
                              style={{ width: 100, padding: '7px 10px', border: '1.5px solid #ddd', borderRadius: 8, fontSize: 14 }}
                            />
                            <button
                              onClick={() => handleSelfSubmit(test)}
                              disabled={selfSubmitSaving}
                              style={{ ...itStyles.selfBtn, background: '#2ECC71', color: 'white', border: 'none' }}
                            >
                              {selfSubmitSaving ? '...' : <><FaPaperPlane /> Submit</>}
                            </button>
                            <button
                              onClick={() => { setSelfSubmitTestId(null); setSelfSubmitError(''); }}
                              style={{ ...itStyles.selfBtn, background: '#f0f0f0', color: '#555', border: 'none' }}
                            >
                              Cancel
                            </button>
                          </div>
                          {selfSubmitError && (
                            <span style={{ color: '#E74C3C', fontSize: 12 }}>{selfSubmitError}</span>
                          )}
                        </div>
                      ) : (
                        <button
                          onClick={() => { setSelfSubmitTestId(test._id); setSelfMarksInput(''); setSelfSubmitError(''); }}
                          style={itStyles.selfBtn}
                        >
                          <FaPaperPlane /> {isSelfRejected ? 'Re-submit Marks' : 'Self-Submit Marks'}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div className="result-card-footer">
                  {statusEl}
                  <span className="result-date">#{idx + 1}</span>
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ marginTop: 14, padding: '12px 16px', background: '#FFF9E6', border: '1px solid #F39C12', borderRadius: 8, fontSize: 13, color: '#7D6608' }}>
          <FaExclamationTriangle style={{ marginRight: 6 }} />
          Self-submitted marks require teacher verification. If not verified, you will be counted as absent for that test.
        </div>
      </div>
    );
  };

  const itStyles = {
    th: { padding: '12px 14px', textAlign: 'left', fontWeight: 700, color: '#2C3E50', fontSize: 13 },
    td: { padding: '12px 14px', fontSize: 13, verticalAlign: 'middle' },
    badge: (color) => ({
      display: 'inline-block', padding: '3px 10px', borderRadius: 10, fontSize: 11,
      fontWeight: 600, background: color + '22', color: color, border: `1px solid ${color}55`
    }),
    selfBtn: {
      display: 'inline-flex', alignItems: 'center', gap: 4,
      background: '#EBF5FB', color: '#2980B9', border: '1px solid #AED6F1',
      borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer'
    }
  };

  const renderAssessmentDetails = () => {
    const result = selectedResult;
    const isYearly = result.assessmentType === 'yearly';
    const isOverall = result.assessmentType === 'overall';
    const avgScore = isYearly ? calculateAverageScore(result.grades) : null;
    
    return (
      <div className="result-details">
        <div className="detail-header">
          <div>
            <h2>{result.assessmentName || 'Assessment Details'}</h2>
            <p className="detail-subtitle">
              {result.courseName} ({result.courseCode})
            </p>
            <div className="assessment-type-badge">
              {isYearly ? (
                <span className="badge-yearly">
                  <FaChartLine /> Yearly (Score-based)
                </span>
              ) : (
                <span className="badge-overall">
                  <FaAward /> Grade-based
                </span>
              )}
            </div>
          </div>
          {getStatusBadge(result.status)}
        </div>

        <div className="detail-info-grid">
          <div className="info-card">
            <span className="info-label">Unit</span>
            <span className="info-value">{result.unit || 'N/A'}</span>
          </div>
          <div className="info-card">
            <span className="info-label">Period</span>
            <span className="info-value">
              {result.fromDate && result.toDate 
                ? `${new Date(result.fromDate).toLocaleDateString()} - ${new Date(result.toDate).toLocaleDateString()}`
                : 'N/A'}
            </span>
          </div>
          <div className="info-card">
            <span className="info-label">Teacher</span>
            <span className="info-value">{result.teacherName}</span>
          </div>
        </div>

        {isYearly && avgScore && (
          <div className="summary-card">
            <h3><FaTrophy /> Performance Summary</h3>
            <div className="summary-stats">
              <div className="stat-item">
                <span className="stat-value">{avgScore}</span>
                <span className="stat-label">Average Score (out of 10)</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{result.grades?.length || 0}</span>
                <span className="stat-label">Total Periods</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">
                  {result.grades?.[0]?.items?.length || 0}
                </span>
                <span className="stat-label">Assessment Items</span>
              </div>
            </div>
            {result.summary?.comments && (
              <div className="comments-section">
                <h4>Teacher's Comments</h4>
                <p>{result.summary.comments}</p>
              </div>
            )}
          </div>
        )}

        {isOverall && (
          <div className="summary-card">
            <h3><FaTrophy /> Grade Summary</h3>
            <div className="info-banner">
              <FaInfoCircle />
              <span>Each assessment item has been graded individually by your teacher.</span>
            </div>
            {result.summary?.comments && (
              <div className="comments-section">
                <h4>Teacher's Comments</h4>
                <p>{result.summary.comments}</p>
              </div>
            )}
          </div>
        )}

        {result.grades && result.grades.length > 0 && (
          <div className="grades-section">
            <h3><FaChartLine /> Detailed {isYearly ? 'Scores' : 'Grades'}</h3>
            
            {isYearly && (
              <div className="yearly-grades-container">
                {result.grades.map((period, idx) => (
                  <div key={idx} className="period-card">
                    <div className="period-header">
                      <h4>{period.periodLabel}</h4>
                      {period.total !== null && period.total !== undefined && (
                        <span className="period-total">
                          Total: <strong>{period.total.toFixed(1)}</strong>
                        </span>
                      )}
                    </div>
                    <div className="grade-items-list">
                      {period.items?.map((item, itemIdx) => (
                        <div key={itemIdx} className="grade-item-row">
                          <span className="grade-item-name">{item.itemName}</span>
                          <span className="score-display">
                            {item.score !== null && item.score !== undefined
                              ? `${item.score}/10`
                              : '—'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {isOverall && result.grades[0] && (
              <div className="overall-grades-container">
                <div className="period-card">
                  <div className="grade-items-list">
                    {result.grades[0].items?.map((item, itemIdx) => (
                      <div key={itemIdx} className="grade-item-row">
                        <span className="grade-item-name">{item.itemName}</span>
                        {item.grade ? (
                          <span
                            className="grade-badge-large"
                            style={{ background: getGradeColor(item.grade), color: 'white' }}
                          >
                            {item.grade}
                          </span>
                        ) : (
                          <span className="not-graded">Not Graded</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderChecklistDetails = () => {
    const result = selectedResult;
    
    return (
      <div className="result-details">
        <div className="detail-header">
          <div>
            <h2>{result.checklistName}</h2>
            <p className="detail-subtitle">
              {result.courseId?.name} ({result.courseId?.code})
            </p>
          </div>
          {getStatusBadge(result.status)}
        </div>

        <div className="detail-info-grid">
          <div className="info-card">
            <span className="info-label">Academic Year</span>
            <span className="info-value">{result.academicYear}</span>
          </div>
          {result.batch && (
            <div className="info-card">
              <span className="info-label">Batch</span>
              <span className="info-value">{result.batch}</span>
            </div>
          )}
          <div className="info-card">
            <span className="info-label">Evaluated By</span>
            <span className="info-value">
              {result.evaluatedBy?.profile?.firstName} {result.evaluatedBy?.profile?.lastName}
            </span>
          </div>
        </div>

        <div className="summary-card">
          <h3><FaTrophy /> Performance Summary</h3>
          <div className="summary-stats">
            <div className="stat-item">
              <span className="stat-value">{result.totalScore}/{result.maxPossibleScore}</span>
              <span className="stat-label">Total Score</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{result.percentageScore?.toFixed(1)}%</span>
              <span className="stat-label">Percentage</span>
            </div>
            <div className="stat-item">
              <span 
                className="stat-value"
                style={{ color: getGradeColor(result.grade) }}
              >
                {result.grade}
              </span>
              <span className="stat-label">Grade</span>
            </div>
          </div>
        </div>

        {result.scores && result.scores.length > 0 && (
          <div className="grades-section">
            <h3><FaCheckCircle /> Observation Items</h3>
            <div className="grade-items-list">
              {result.scores.map((item, idx) => {
                const comment = result.itemComments?.find(c => c.itemId === item.itemId);
                return (
                  <div key={idx} className={`grade-item-row${comment?.comment ? ' grade-item-row--with-comment' : ''}`}>
                    <div className="grade-item-main">
                      <span className="grade-item-name">{item.itemName}</span>
                      <span className="score-badge">{item.score}/10</span>
                    </div>
                    {comment?.comment && (
                      <p className="grade-item-comment">{comment.comment}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {result.overallComments && (
          <div className="comments-section-standalone">
            <h4>Overall Comments</h4>
            <p>{result.overallComments}</p>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="results-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading results...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="results-container">
        <div className="error-state">
          <p>{error}</p>
          <button onClick={() => onNavigate('dashboard')} className="back-button24">
            <FaArrowLeft /> Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="results-container">
      <div className="results-header">
        <button onClick={() => {
          if (viewMode === 'detail') {
            setViewMode('list');
            setSelectedResult(null);
          } else {
            onNavigate('dashboard');
          }
        }} className="back-button24">
          <FaArrowLeft /> {viewMode === 'detail' ? 'Back to List' : 'Back to Dashboard'}
        </button>
        <h1>My Results</h1>
      </div>

      {viewMode === 'list' && (
        <>
          <div className="tabs-container">
            <button 
              className={`tab ${activeTab === 'assessments' ? 'active' : ''}`}
              onClick={() => setActiveTab('assessments')}
            >
              <FaBook /> Assessments ({assessmentResults.length})
            </button>
            <button
              className={`tab ${activeTab === 'checklists' ? 'active' : ''}`}
              onClick={() => setActiveTab('checklists')}
            >
              <FaClipboardList /> Checklists ({checklistResults.length})
            </button>
            {internalTestEnabled && (
              <button
                className={`tab ${activeTab === 'internal-tests' ? 'active' : ''}`}
                onClick={() => setActiveTab('internal-tests')}
              >
                <FaFileAlt /> Internal Tests ({internalTests.length})
              </button>
            )}
          </div>

          <div className="results-content">
            {activeTab === 'assessments' && renderAssessmentsList()}
            {activeTab === 'checklists' && renderChecklistsList()}
            {activeTab === 'internal-tests' && internalTestEnabled && renderInternalTests()}
          </div>
        </>
      )}

      {viewMode === 'detail' && selectedResult && (
        <div className="results-content">
          {selectedResult.type === 'assessment' && renderAssessmentDetails()}
          {selectedResult.type === 'checklist' && renderChecklistDetails()}
        </div>
      )}
    </div>
  );
};

export default Results;